import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Input } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Grid, GridColumn, GridCustomCellProps } from "@progress/kendo-react-grid";
import { pencilIcon, trashIcon, folderIcon } from "@progress/kendo-svg-icons";
import { useInjection } from "@presentation/components/dependencyInjection";
import { StorageConfigurationService } from "@application/useCases/workspace/StorageConfigurationService";
import { extractProblemDetails } from "@presentation/common/ErrorHelpers";
import { useNotifications } from "@presentation/components/notifications";
import { EditStoragePathDialog } from "@presentation/components/storageConfiguration/EditStoragePathDialog";
import { FolderPickerDialog, RESULTS_PATH_BROWSE_PATH_TYPE } from "@presentation/components/storageConfiguration/FolderPickerDialog";
import { isValidStoragePath, STORAGE_PATH_HELP_TEXT } from "@presentation/components/storageConfiguration/storagePathValidation";
import type { EffectiveStorageContextConfiguration, PathTypeSchema, PathVisibilitySchema, StorageContextConfiguration } from "@domain/schemas/BI";

// `BI.schema.d.ts` enums have no runtime object under Vitest (esbuild treats a `.d.ts` file's enum
// as ambient/type-only; Vite's own esbuild transpiles it anyway, so production is unaffected) -
// string literals cast to the enum type sidestep that entirely, matching this codebase's
// established workaround (see SettingsOriginPill in GenericMultiSelectSettingsForm.tsx).
const VISIBILITY_SHARED = "Shared" as PathVisibilitySchema;
const VISIBILITY_PRIVATE = "Private" as PathVisibilitySchema;
const FACETS = ["Raw", "Fasta", "SoftwareSettings", "Libraries"] as PathTypeSchema[];

interface StorageConfigurationSectionProps {
  level: "team" | "project";
  contextId: string;
  /**
  Own-rows CRUD controls are hidden (never disabled) when false, per WP4.0.3's rendering convention.
  */
  canManage: boolean;
}

/**
Own and inherited paths rendered as rows of one table per facet, not two visually distinct blocks -
an "Origin" column (Own/Inherited) carries that distinction instead. A facet with nothing resolved
still renders as a single placeholder row (`-`), rather than swapping the table out for plain text.
*/
type DisplayPathRow = { key: string; id: string | null; path: string; visibility: PathVisibilitySchema | null; isOwn: boolean; isPlaceholder?: boolean };

/**
 * Own-rows CRUD (add/remove/toggle Visibility) per facet, plus a read-only effective (resolved)
 * view showing each path's Visibility and whether it is this context's own override or inherited
 * from a parent level (WP5.5). The backend's `EffectivePathDTO` carries no source-level field for
 * storage paths (unlike software versions), so "own vs inherited" is derived client-side by
 * comparing each effective path against this context's own override rows.
 */
export const StorageConfigurationSection: React.FC<StorageConfigurationSectionProps> = ({ level, contextId, canManage }) => {
  const storageConfigurationService = useInjection(StorageConfigurationService);
  const { pushNotification } = useNotifications();
  const [ownConfiguration, setOwnConfiguration] = useState<StorageContextConfiguration | null>(null);
  const [effectiveConfiguration, setEffectiveConfiguration] = useState<EffectiveStorageContextConfiguration | null>(null);
  const [newPathByFacet, setNewPathByFacet] = useState<Partial<Record<PathTypeSchema, string>>>({});
  const [newVisibilityByFacet, setNewVisibilityByFacet] = useState<Partial<Record<PathTypeSchema, PathVisibilitySchema>>>({});
  const [resultsPath, setResultsPath] = useState("");
  const [editingPath, setEditingPath] = useState<{ id: string; path: string; pathType: PathTypeSchema } | null>(null);
  const [browsingFacet, setBrowsingFacet] = useState<PathTypeSchema | null>(null);
  const [browsingResultsPath, setBrowsingResultsPath] = useState(false);

  /* eslint-disable unicorn/no-declarations-before-early-exit -- every handler below is used by the JSX returned after the component's own early return */
  const reload = useCallback(async () => {
    const effective =
      level === "team"
        ? await storageConfigurationService.getTeamEffectiveStorageConfigurationAsync(contextId)
        : await storageConfigurationService.getProjectEffectiveStorageConfigurationAsync(contextId);
    setEffectiveConfiguration(effective);

    if (!canManage) return;
    const own = level === "team" ? await storageConfigurationService.getTeamStorageConfigurationAsync(contextId) : await storageConfigurationService.getProjectStorageConfigurationAsync(contextId);
    setOwnConfiguration(own);
    setResultsPath(own.resultsPath ?? "");
  }, [canManage, contextId, level, storageConfigurationService]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const handleAddPath = async (pathType: PathTypeSchema) => {
    const path = (newPathByFacet[pathType] ?? "").trim();
    if (!path) return;
    if (!isValidStoragePath(path)) {
      pushNotification(STORAGE_PATH_HELP_TEXT, "error", 10_000);
      return;
    }

    const visibility = newVisibilityByFacet[pathType] ?? VISIBILITY_SHARED;
    try {
      const updated =
        level === "team"
          ? await storageConfigurationService.addTeamPathAsync(contextId, pathType, path, visibility)
          : await storageConfigurationService.addProjectPathAsync(contextId, pathType, path, visibility);
      setOwnConfiguration(updated);
      setNewPathByFacet((state) => ({ ...state, [pathType]: "" }));
      void reload();
    } catch (error) {
      // Surfaced verbatim: a conflict rejection already names the owning Team/Project.
      pushNotification(extractProblemDetails(error).detail ?? extractProblemDetails(error).title ?? "Could not add this path.", "error", 10_000);
    }
  };

  const handleRemovePath = async (pathId: string) => {
    try {
      const updated = level === "team" ? await storageConfigurationService.removeTeamPathAsync(contextId, pathId) : await storageConfigurationService.removeProjectPathAsync(contextId, pathId);
      setOwnConfiguration(updated);
      void reload();
    } catch (error) {
      pushNotification(extractProblemDetails(error).detail ?? extractProblemDetails(error).title ?? "Could not remove this path.", "error", 10_000);
    }
  };

  // Changes the path's value in place (no remove/re-add), so it never hits the
  // overlap-with-itself or at-least-one-row-per-facet conflicts a naive remove-then-add would.
  const handleChangePath = async (pathId: string, newPath: string) => {
    const updated =
      level === "team" ? await storageConfigurationService.setTeamPathValueAsync(contextId, pathId, newPath) : await storageConfigurationService.setProjectPathValueAsync(contextId, pathId, newPath);
    setOwnConfiguration(updated);
    void reload();
  };

  const handleToggleVisibility = async (pathId: string, currentVisibility: PathVisibilitySchema) => {
    const nextVisibility = currentVisibility === VISIBILITY_SHARED ? VISIBILITY_PRIVATE : VISIBILITY_SHARED;
    try {
      const updated =
        level === "team"
          ? await storageConfigurationService.setTeamPathVisibilityAsync(contextId, pathId, nextVisibility)
          : await storageConfigurationService.setProjectPathVisibilityAsync(contextId, pathId, nextVisibility);
      setOwnConfiguration(updated);
      void reload();
    } catch (error) {
      pushNotification(extractProblemDetails(error).detail ?? extractProblemDetails(error).title ?? "Could not change this path's visibility.", "error", 10_000);
    }
  };

  const handleSaveResultsPath = async () => {
    try {
      const updated =
        level === "team"
          ? await storageConfigurationService.setTeamResultsPathAsync(contextId, resultsPath.trim())
          : await storageConfigurationService.setProjectResultsPathAsync(contextId, resultsPath.trim());
      setOwnConfiguration(updated);
      void reload();
    } catch (error) {
      pushNotification(extractProblemDetails(error).detail ?? extractProblemDetails(error).title ?? "Could not save the results path.", "error", 10_000);
    }
  };
  /* eslint-enable unicorn/no-declarations-before-early-exit */

  if (!effectiveConfiguration) return null;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "grid", gap: 8 }}>
        <strong>Results path</strong>
        {canManage && (
          <div style={{ display: "flex", gap: 8 }}>
            <Input placeholder="Results path (blank to inherit)" style={{ flex: 1, minWidth: 0 }} value={resultsPath} onChange={(event) => setResultsPath(String(event.value ?? ""))} />
            <Button type="button" onClick={() => void handleSaveResultsPath()}>
              Save
            </Button>
            <Button type="button" fillMode="flat" svgIcon={folderIcon} title="Browse results path" aria-label="Browse results path" onClick={() => setBrowsingResultsPath(true)} />
          </div>
        )}
        <span>
          Effective: {effectiveConfiguration.resultsPath ?? "-"} <span style={{ color: "#64748b" }}>({ownConfiguration?.resultsPath ? "Own" : "Inherited"})</span>
        </span>
      </div>

      {FACETS.map((facet) => {
        const ownPaths = ownConfiguration?.paths?.filter((path) => path.pathType === facet) ?? [];
        const effectivePaths = effectiveConfiguration.paths?.[facet] ?? [];
        // Own override rows carry full edit controls; everything else resolved for this facet is
        // inherited from a parent level - shown in the same table, just read-only and labeled as such.
        const inheritedPaths = effectivePaths.filter((path) => ownPaths.every((ownPath) => ownPath.path !== path.path));
        const rows: DisplayPathRow[] = [
          ...ownPaths.map((path) => ({ key: path.id!, id: path.id!, path: path.path!, visibility: path.visibility!, isOwn: true })),
          ...inheritedPaths.map((path, index) => ({ key: `inherited-${index}`, id: null, path: path.path!, visibility: path.visibility!, isOwn: false })),
        ];
        if (rows.length === 0) rows.push({ key: "empty", id: null, path: "-", visibility: null, isOwn: false, isPlaceholder: true });

        return (
          <div key={facet} style={{ display: "grid", gap: 8 }}>
            <strong>{facet}</strong>

            <Grid data={rows} dataItemKey="key">
              <GridColumn field="path" title="Path" />
              <GridColumn title="Visibility" width="120px" cell={(properties: GridCustomCellProps) => <td>{(properties.dataItem as DisplayPathRow).visibility ?? "-"}</td>} />
              <GridColumn
                title="Origin"
                width="100px"
                cell={(properties: GridCustomCellProps) => {
                  const row = properties.dataItem as DisplayPathRow;
                  const origin = row.isPlaceholder ? "-" : row.isOwn ? "Own" : "Inherited"; // eslint-disable-line unicorn/no-nested-ternary -- three mutually exclusive origins mapped to labels
                  return <td>{origin}</td>;
                }}
              />
              {canManage && (
                <GridColumn
                  title=""
                  width="260px"
                  cell={(properties: GridCustomCellProps) => {
                    const row = properties.dataItem as DisplayPathRow;
                    if (!row.isOwn) return <td />;
                    return (
                      <td>
                        <div style={{ display: "flex", gap: 8 }}>
                          <Button
                            type="button"
                            fillMode="flat"
                            title={
                              row.visibility === VISIBILITY_SHARED
                                ? "Reserve this path for this context. Other teams or projects cannot configure overlapping paths, except where team/project inheritance allows it. Existing overlapping configurations may prevent this change."
                                : "Release this context's private claim so other teams or projects can configure overlapping shared paths. This does not change team or project membership."
                            }
                            onClick={() => handleToggleVisibility(row.id!, row.visibility!)}
                          >
                            {row.visibility === VISIBILITY_SHARED ? "Make Private" : "Make Shared"}
                          </Button>
                          <Button
                            type="button"
                            fillMode="flat"
                            svgIcon={pencilIcon}
                            title="Change path"
                            aria-label="Change path"
                            onClick={() => setEditingPath({ id: row.id!, path: row.path, pathType: facet })}
                          />
                          <Button type="button" fillMode="flat" themeColor="error" svgIcon={trashIcon} title="Remove" aria-label="Remove" onClick={() => handleRemovePath(row.id!)} />
                        </div>
                      </td>
                    );
                  }}
                />
              )}
            </Grid>

            {canManage && (
              <div style={{ display: "grid", gap: 4 }}>
                <div style={{ display: "flex", gap: 8 }}>
                  <Input
                    placeholder="Path"
                    style={{ flex: 1, minWidth: 0 }}
                    value={newPathByFacet[facet] ?? ""}
                    onChange={(event) => setNewPathByFacet((state) => ({ ...state, [facet]: String(event.value ?? "") }))}
                  />
                  <DropDownList
                    style={{ flex: "0 0 auto", width: 130 }}
                    data={[VISIBILITY_SHARED, VISIBILITY_PRIVATE]}
                    value={newVisibilityByFacet[facet] ?? VISIBILITY_SHARED}
                    onChange={(event) => setNewVisibilityByFacet((state) => ({ ...state, [facet]: event.value as PathVisibilitySchema }))}
                  />
                  <Button type="button" disabled={!isValidStoragePath((newPathByFacet[facet] ?? "").trim())} onClick={() => void handleAddPath(facet)}>
                    Add
                  </Button>
                  <Button type="button" fillMode="flat" svgIcon={folderIcon} title="Browse" aria-label="Browse" onClick={() => setBrowsingFacet(facet)} />
                </div>
                <span style={{ fontSize: 12, color: "#64748b" }}>{STORAGE_PATH_HELP_TEXT}</span>
              </div>
            )}
          </div>
        );
      })}

      {editingPath && (
        <EditStoragePathDialog
          currentPath={editingPath.path}
          pathType={editingPath.pathType}
          listFoldersAsync={(pathType, folder) => storageConfigurationService.listFoldersAsync(pathType, folder)}
          onClose={() => setEditingPath(null)}
          onSubmit={(newPath) => handleChangePath(editingPath.id, newPath)}
        />
      )}
      {browsingFacet && (
        <FolderPickerDialog
          pathType={browsingFacet}
          initialFolder={newPathByFacet[browsingFacet] ?? ""}
          listFoldersAsync={(pathType, folder) => storageConfigurationService.listFoldersAsync(pathType, folder)}
          onSelect={(folder) => {
            setNewPathByFacet((state) => ({ ...state, [browsingFacet]: folder }));
            setBrowsingFacet(null);
          }}
          onClose={() => setBrowsingFacet(null)}
        />
      )}
      {browsingResultsPath && (
        <FolderPickerDialog
          pathType={RESULTS_PATH_BROWSE_PATH_TYPE}
          initialFolder={resultsPath}
          listFoldersAsync={(pathType, folder) => storageConfigurationService.listFoldersAsync(pathType, folder)}
          onSelect={(folder) => {
            setResultsPath(folder);
            setBrowsingResultsPath(false);
          }}
          onClose={() => setBrowsingResultsPath(false)}
        />
      )}
    </div>
  );
};

export default StorageConfigurationSection;
